import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { del, patch } from "../../lib/api";
import {
  IDENTITY_EXTERNAL_REF_MAX,
  IDENTITY_LABEL_MAX,
  identityLabelError,
  tokenTypeError,
} from "../../lib/identity";
import { useSession } from "../../lib/session";
import type { Identity, IdentityDetail, TokenType } from "../../lib/types";
import { HOLDERS_KEY, HolderSelect } from "../holders/HolderSelect";
import { Button } from "../ui/Button";
import { ButtonGroup } from "../ui/ButtonGroup";
import { ErrorNotice, Notice } from "../ui/Feedback";
import { Field } from "../ui/Input";
import { Modal } from "../ui/Modal";
import { MicroLabel } from "../ui/Text";

/**
 * Correcting what a card is called, whose it is and what kind of token it is,
 * and removing one that should never have been entered. The edit is a modal, so
 * it opens from the identities list as readily as from the detail page.
 *
 * ## Two operations, one component
 *
 * They are the two halves of the same repair and the operator picks between them
 * by what went wrong. A wrong *name* — a typo, a van changing hands, a contractor
 * card reissued — is an edit. A wrong *ID tag* is not: the tag is the string every
 * session was matched on, so the server refuses to change it and renaming would
 * only hide the mistake. The honest repair there is to delete the card and enter
 * the right one, which is safe in exactly the case where there is no history to
 * misattribute.
 *
 * Putting them side by side is what makes that choice legible. A page offering
 * only "edit" invites someone to rename a card onto a tag it does not have.
 *
 * ## Why the delete button is not always there
 *
 * `sessionCount` comes from the detail response and is the same count the server's
 * guard reads. A card that has charged cannot be deleted at any point in the
 * future, so offering the button and answering 409 would be a control that exists
 * to refuse. The sentence that replaces it names the remedy instead.
 *
 * It is a page-load figure, so a truck plugging in after the page rendered can make
 * it stale. That is why the server checks again under a row lock and why the
 * confirmation below claims nothing about what this card has done — a refusal
 * refetches and the explanation takes the button's place on the next render.
 */

export function EditIdentity({ identity }: { readonly identity: IdentityDetail }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState<string | undefined>(undefined);

  const canUpdate = can("identities.update");
  const canDelete = can("identities.delete");

  if (!canUpdate && !canDelete) {
    return (
      <MicroLabel>
        Renaming or removing a card needs the Edit cards or Remove cards permission
      </MicroLabel>
    );
  }

  return (
    <div className="flex flex-col gap-4 border-t border-rule pt-4">
      {saved === undefined ? null : <Notice tone="ok">{saved}</Notice>}

      <div className="flex flex-wrap items-center gap-3">
        {canUpdate ? (
          <Button
            onClick={() => {
              setSaved(undefined);
              setEditing(true);
            }}
          >
            Edit this card
          </Button>
        ) : null}

        {canDelete ? (
          <DeleteIdentity
            identity={identity}
            onDeleted={() => {
              void queryClient.invalidateQueries({ queryKey: ["identities"] });
              void queryClient.invalidateQueries({ queryKey: HOLDERS_KEY });
              /**
               * Replace, not push. The page just deleted is now a 404, and leaving
               * it in history means the back button lands an operator on an error
               * for a card they removed on purpose.
               */
              navigate("/identities", { replace: true });
            }}
          />
        ) : null}
      </div>

      {canUpdate ? (
        <EditIdentityModal
          identity={identity}
          open={editing}
          onOpenChange={setEditing}
          onSaved={(updated) => {
            setEditing(false);
            setSaved(`Saved. This card is now ${updated.label}.`);
          }}
        />
      ) : null}
    </div>
  );
}

const TOKEN_TYPE_OPTIONS = [
  { value: "rfid", label: "RFID card" },
  { value: "vin", label: "Vehicle VIN" },
] as const;

/**
 * The edit itself. Mounted only while open, so every opening starts from the
 * card as it is now rather than from a half-typed earlier attempt.
 */
export function EditIdentityModal({
  identity,
  open,
  onOpenChange,
  onSaved,
}: {
  readonly identity: Identity;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSaved: (updated: Identity) => void;
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      eyebrow={`Edit · ${identity.idTag}`}
      title={identity.label}
    >
      {open ? (
        <EditForm identity={identity} onCancel={() => onOpenChange(false)} onSaved={onSaved} />
      ) : null}
    </Modal>
  );
}

function EditForm({
  identity,
  onCancel,
  onSaved,
}: {
  readonly identity: Identity;
  readonly onCancel: () => void;
  readonly onSaved: (updated: Identity) => void;
}) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(identity.label);
  const [externalRef, setExternalRef] = useState(identity.externalRef ?? "");
  const [holderId, setHolderId] = useState(identity.holder.id);
  const [tokenType, setTokenType] = useState<TokenType>(identity.tokenType);

  const save = useMutation({
    mutationFn: (body: unknown) => patch<Identity>(`/identities/${identity.id}`, body),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ["identity", identity.id] });
      // The list shows every card's label, holder and token on every filtered
      // page, and a move changes both holders' card counts.
      void queryClient.invalidateQueries({ queryKey: ["identities"] });
      void queryClient.invalidateQueries({ queryKey: HOLDERS_KEY });
      onSaved(updated);
    },
  });

  const labelIssue = identityLabelError(label);
  const tokenIssue = tokenTypeError(identity.idTag, tokenType);
  const canSubmit =
    label.trim() !== "" && labelIssue === undefined && tokenIssue === undefined && !save.isPending;
  // Offered only when the stored tag already qualifies; otherwise the option is
  // shown greyed with the reason, so the operator sees why rather than a refusal.
  const vinAllowed = tokenTypeError(identity.idTag, "vin") === undefined;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;

        save.mutate({
          label: label.trim(),
          // Only when it changes, so a rename is not also audited as a move.
          ...(holderId === identity.holder.id ? {} : { holderId }),
          ...(tokenType === identity.tokenType ? {} : { tokenType }),
          // A blank clears it; the server reads "" as none.
          ...(externalRef.trim() === (identity.externalRef ?? "")
            ? {}
            : { externalRef: externalRef.trim() }),
        });
      }}
      className="flex flex-col gap-5"
    >
      {save.isError ? <ErrorNotice error={save.error} /> : null}

      <Field
        label="Label"
        value={label}
        onChange={(event) => setLabel(event.target.value)}
        maxLength={IDENTITY_LABEL_MAX}
        required
        autoFocus
        hint="Personal data. The change is recorded in the audit trail with the old name and the new one."
        {...(labelIssue === undefined ? {} : { error: labelIssue })}
      />

      <Field
        label="External ref"
        value={externalRef}
        onChange={(event) => setExternalRef(event.target.value)}
        maxLength={IDENTITY_EXTERNAL_REF_MAX}
        hint="Optional. What a partner calls this card or vehicle — sent as the plate when sessions are pushed. Empty sends the label."
      />

      <ButtonGroup
        label="Token"
        value={tokenType}
        onChange={setTokenType}
        options={TOKEN_TYPE_OPTIONS.map((option) =>
          option.value === "vin" && !vinAllowed ? { ...option, disabled: true } : option,
        )}
        hint={
          vinAllowed
            ? "What the tag is, not what it says. Changing it does not change how a station authorises it."
            : "Only a tag that is already a VIN — 17 capital letters and digits, no I, O or Q — can be marked as one."
        }
        {...(tokenIssue === undefined ? {} : { error: tokenIssue })}
      />

      <HolderSelect
        label="Holder"
        value={holderId}
        onChange={setHolderId}
        required
        hint="Moving a card moves its future sessions to the new holder's pool. Sessions already recorded stay where they were."
      />

      {/* The tag is stated here, greyed and uneditable, rather than left off the
          form. An operator who came to fix a mistyped tag has to be able to see
          that this is not where that happens — and what to do instead. */}
      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        The ID tag stays {identity.idTag}. It is what a charge point presents and what every session
        on this card was matched on, so it cannot be edited. A card entered against the wrong tag is
        removed and entered again.
      </p>

      <div className="flex items-center justify-end gap-2 border-t border-rule pt-4">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={!canSubmit} pending={save.isPending}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

export function DeleteIdentity({
  identity,
  onDeleted,
}: {
  readonly identity: IdentityDetail;
  readonly onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const remove = useMutation({
    mutationFn: () => del(`/identities/${identity.id}`),
    onSuccess: onDeleted,
    /**
     * The commonest failure here is a 409 because a session started since this page
     * loaded, and `sessionCount` is now stale — which is exactly what decided
     * whether the button was offered. Refetching turns the next render into the
     * explanation below rather than leaving a control that will refuse again.
     */
    onError: () => void queryClient.invalidateQueries({ queryKey: ["identity", identity.id] }),
  });

  if (identity.sessionCount > 0) {
    return (
      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        {identity.sessionCount === 1
          ? "This card has one recorded session"
          : `This card has ${identity.sessionCount} recorded sessions`}
        , so it cannot be deleted — that energy is attributed through it. Block it instead if it
        should stop working.
      </p>
    );
  }

  return (
    <div className="flex flex-col items-start gap-3">
      {/* Beside the control, not instead of it. Replacing the button with the
          error leaves an operator holding a message and no way to act on it. */}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      {confirming ? (
        <div className="flex flex-wrap items-center gap-3">
          {/* The name, not "this card". A confirmation that does not say what it is
              about to destroy is one an operator learns to click through.

              What it does *not* claim is that the card has never charged. That was
              true when the page loaded and a truck may have plugged in since; the
              server checks again under a row lock and refuses if so, and a sentence
              asserting a fact this page cannot still vouch for would be the one
              part of the exchange that lies. */}
          <span className="text-body text-ink-muted">
            Delete {identity.label} ({identity.idTag}) for good? It stops authorising immediately,
            and only a card that has never charged can go.
          </span>
          <Button
            variant="danger"
            pending={remove.isPending}
            onClick={() => {
              setConfirming(false);
              remove.mutate();
            }}
          >
            Confirm deletion
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button variant="danger" onClick={() => setConfirming(true)}>
          Delete this card
        </Button>
      )}
    </div>
  );
}
